// Domain ancestor helpers. We don't ship the full Public Suffix List, so
// the stop condition for parent traversal is "two labels remaining" —
// for plain TLDs (`.com`, `.org`) this lands on the apex; for two-label
// public suffixes (`.com.br`, `.co.uk`) we walk one level too deep and
// hit a public suffix that obviously won't have any TXT records. That's
// a single extra DoH query in the worst case, harmless.
//
// `organizationalDomain` is the eTLD+1 — the label that "owns" all the
// subdomains in DMARC reports. Used to collapse subdomain rows into the
// root they belong to (e.g. cc.plusdin.com.br → plusdin.com.br). Uses a
// curated 2-label PSL covering common Latin/UK/AU/JP/etc. country code
// suffixes — not every PSL entry, but enough to cover the cases we
// monitor in practice without bundling a 100KB list.

const TWO_LABEL_PSL = new Set<string>([
  // Brazil
  "com.br", "net.br", "org.br", "gov.br", "edu.br", "art.br", "blog.br",
  // Argentina
  "com.ar", "net.ar", "org.ar", "gov.ar", "edu.ar",
  // Mexico
  "com.mx", "net.mx", "org.mx", "gob.mx", "edu.mx",
  // Colombia / Chile / Peru / Uruguay / Venezuela
  "com.co", "net.co", "org.co", "gov.co", "edu.co",
  "com.cl", "co.cl",
  "com.pe", "net.pe", "org.pe", "gob.pe", "edu.pe",
  "com.uy", "net.uy", "org.uy", "edu.uy",
  "com.ve", "net.ve", "org.ve", "edu.ve",
  // UK / IE
  "co.uk", "org.uk", "ac.uk", "gov.uk", "ltd.uk", "plc.uk", "me.uk", "net.uk",
  "co.ie",
  // Australia / New Zealand
  "com.au", "net.au", "org.au", "edu.au", "gov.au",
  "co.nz", "net.nz", "org.nz", "govt.nz",
  // Asia
  "co.jp", "ne.jp", "or.jp", "ac.jp", "go.jp", "co.kr", "or.kr", "ac.kr",
  "com.cn", "net.cn", "org.cn", "gov.cn", "edu.cn",
  "com.hk", "com.tw", "com.sg", "com.my", "com.ph", "com.th", "com.id",
  "com.vn",
  // South Africa / Israel / Turkey / Russia
  "co.za", "org.za", "gov.za",
  "co.il", "ac.il", "gov.il",
  "com.tr", "edu.tr", "gov.tr",
  "com.ru", "net.ru", "org.ru",
  // EU edge cases that come up
  "co.at", "or.at", "ac.at",
]);

export function immediateParent(domain: string): string | null {
  const parts = domain.split(".").filter(Boolean);
  if (parts.length <= 2) return null;
  return parts.slice(1).join(".");
}

export function parentChain(domain: string): string[] {
  const parts = domain.split(".").filter(Boolean);
  const out: string[] = [];
  for (let i = 1; i < parts.length - 1; i++) {
    out.push(parts.slice(i).join("."));
  }
  return out;
}

// eTLD+1 — the organizational domain that owns the input. Used to
// collapse subdomain DMARC records into the root they share.
//
// Algorithm: lowercase + split on dots. If the last two labels match a
// known 2-label public suffix (com.br, co.uk, …), the org domain is the
// last 3 labels. Otherwise it's the last 2. Returns the input unchanged
// when it has 2 labels or fewer (already at the apex).
export function organizationalDomain(domain: string): string {
  const parts = domain.toLowerCase().trim().replace(/\.$/, "").split(".").filter(Boolean);
  if (parts.length <= 2) return parts.join(".");
  const last2 = parts.slice(-2).join(".");
  if (TWO_LABEL_PSL.has(last2)) {
    return parts.slice(-3).join(".");
  }
  return parts.slice(-2).join(".");
}

// True when the candidate domain is at-or-below the org domain of
// `root` — i.e. they share the same organizational domain. Useful when
// matching subdomain header_from values to a monitored root.
export function isSameOrganization(domainA: string, domainB: string): boolean {
  return organizationalDomain(domainA) === organizationalDomain(domainB);
}
